import React, { useState, useEffect, useCallback } from "react";
import { useBluelagoon } from "@bluelagoon";
import { useTranslation } from "react-i18next";
import TestingHeader from "./TestingHeader";
import TestingSummary from "./TestingSummary";
import TestingCaseList from "./TestingCaseList";
import TestingResultModal from "./TestingResultModal";

function TestingPanel({ workflowId, stepId }) {
    const { t } = useTranslation();
    const [testCases, setTestCases] = useState([]);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [latestRun, setLatestRun] = useState(null);
    const [isRunning, setIsRunning] = useState(false);
    const [resultModalOpen, setResultModalOpen] = useState(false);

    const { call: fetchTests, isLoading } = useBluelagoon({
        method: "getWorkflowTestCases",
    });
    const { call: runTests } = useBluelagoon({ method: "runWorkflowTests" });
    const { call: fetchRuns } = useBluelagoon({ method: "getTestRuns" });
    const { call: fetchRun } = useBluelagoon({ method: "getTestRun" });

    const loadData = useCallback(async () => {
        if (!workflowId) return;
        const [testsRes, runsRes] = await Promise.all([
            fetchTests({ workflowId }),
            fetchRuns({ workflowId }),
        ]);
        if (testsRes?.testCases) setTestCases(testsRes.testCases);
        if (runsRes?.testRuns?.[0]) setLatestRun(runsRes.testRuns[0]);
    }, [workflowId, fetchTests, fetchRuns]);

    useEffect(() => { loadData(); }, [loadData]);

    // Poll when running
    useEffect(() => {
        if (!isRunning || !latestRun?.id) return;
        const interval = setInterval(async () => {
            const res = await fetchRun({ id: latestRun.id });
            if (res?.testRun) {
                setLatestRun(res.testRun);
                if (["passed", "failed", "error"].includes(res.testRun.status)) {
                    setIsRunning(false);
                    loadData();
                }
            }
        }, 3000);
        return () => clearInterval(interval);
    }, [isRunning, latestRun?.id, fetchRun, loadData]);

    const handleRunAll = useCallback(async () => {
        setIsRunning(true);
        const res = await runTests({ workflowId });
        if (res?.testRun) setLatestRun(res.testRun);
    }, [workflowId, runTests]);

    const handleRunSelected = useCallback(async () => {
        if (selectedIds.size === 0) return;
        setIsRunning(true);
        const res = await runTests({
            workflowId,
            testCaseIds: Array.from(selectedIds),
        });
        if (res?.testRun) setLatestRun(res.testRun);
    }, [workflowId, selectedIds, runTests]);

    const toggleSelect = useCallback((id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }, []);

    return (
        <div className="w-full h-full overflow-auto bg-gray-50 p-4 md:p-6 pb-8">
            <div className="max-w-4xl mx-auto space-y-4">
                <TestingHeader
                    onRunAll={handleRunAll}
                    isRunning={isRunning}
                    testCount={testCases.length}
                />
                <TestingSummary latestRun={latestRun} isRunning={isRunning} />
                <TestingCaseList
                    testCases={testCases}
                    selectedIds={selectedIds}
                    onToggleSelect={toggleSelect}
                    onRunSelected={handleRunSelected}
                    isRunning={isRunning}
                    isLoading={isLoading}
                    onViewResults={() => setResultModalOpen(true)}
                />
                {resultModalOpen && latestRun && (
                    <TestingResultModal
                        testRun={latestRun}
                        onClose={() => setResultModalOpen(false)}
                    />
                )}
            </div>
        </div>
    );
}

export default TestingPanel;
